import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchAllSizeCategories } from '@store/actions/MeasurementAction';

import {
	addNewMeasurementSize,
	fetchSizeCategoryById,
	removeSizeCategoryById,
	updateMeasurementSizeById
} from '@services/measurementService';

import { generateRandomString, insertAt } from '@utils/Util';

const AddSizeCategory = ({ onClose, selectedId, onAddSuccess }) => {
	const designToolStore = useSelector((store) => store.designToolStore);
	const [removeProductSizeIdList, setRemoveProductSizeIdList] = useState([]);
	const [editableFieldName, setEditableFieldName] = useState('');
	const [duplicateLabels, setDuplicateLabels] = useState({});
	const [baseSizeUid, setBaseSizeUid] = useState(null);
	const [editableUid, setEditableUid] = useState(null);
	const [inputData, setInputData] = useState({});
	const [tableData, setTableData] = useState([]);
	const [errors, setErrors] = useState({});
	const dispatch = useDispatch();
	const { id } = useParams();

	useEffect(() => {
		(async () => {
			if (selectedId) {
				try {
					dispatch(openLoader());
					const data = await fetchSizeCategoryById(selectedId);

					const sizeLabelList = data.productSizeList?.map((item) => ({
						isHidden: item.isDisabled,
						sizeLabel: item.label,
						remarks: item.remarks,
						uId: generateRandomString(),
						id: item.id
					}));

					const baseSize = data.productSizeList.find((item) => item.isBaseSize);

					setInputData({
						sizeName: data.name,
						sizeRange: data.range,
						baseSize: baseSize ? { id: baseSize.label, value: baseSize.label } : {}
					});
					let selectedBaseSize = sizeLabelList.find((item) => item.sizeLabel === baseSize?.label);
					setBaseSizeUid(selectedBaseSize?.uId);
					setTableData(sizeLabelList);
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			}
		})();
		if (!selectedId) {
			setTableData([
				{
					isHidden: false,
					sizeLabel: '',
					remarks: '',
					uId: generateRandomString()
				},
				{
					isHidden: false,
					sizeLabel: '',
					remarks: '',
					uId: generateRandomString()
				}
			]);
		}
	}, [selectedId]);

	const addNewRow = () => {
		setTableData((prev) => [
			...prev,
			{
				isHidden: false,
				sizeLabel: '',
				remarks: '',
				uId: generateRandomString()
			}
		]);
	};

	const removeRow = async (size) => {
		const clearBaseSize = () => {
			setTableData((prev) => prev.filter((item) => item.uId !== size.uId));
			if (inputData?.baseSize?.value === size.sizeLabel) {
				setInputData((prev) => ({ ...prev, baseSize: {} }));
			}
		};

		if (size.id) {
			try {
				dispatch(openLoader());
				const data = await removeSizeCategoryById(selectedId, size.id);
				if (data === true) {
					clearBaseSize();
					setRemoveProductSizeIdList((prev) => [...prev, size.id]);
				}
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		} else {
			clearBaseSize();
		}
	};

	const toggleHidden = (uId) => {
		let modifiedData = tableData.map((item) => {
			if (item.uId === uId) {
				item.isHidden = !item.isHidden;
			}
			return item;
		});
		setTableData(modifiedData);
	};

	const handleChange = (e, uId) => {
		let modifiedData = tableData?.map((item) => {
			if (item.uId === uId) {
				item[e.target.name] = e.target.value;
			}
			return item;
		});
		if (baseSizeUid === uId && e.target.name === 'sizeLabel') {
			setInputData((prev) => ({ ...prev, baseSize: {} }));
		}

		setTableData(modifiedData);
	};

	const renderTableBody = () => {
		return tableData?.map((item, index) => {
			return (
				<Draggable key={`table_row_${item.uId}`} draggableId={item.uId} index={index}>
					{(provided) => (
						<tr
							key={index}
							ref={provided.innerRef}
							{...provided.draggableProps}
							{...provided.dragHandleProps}
							tabIndex={-1}
						>
							<td>
								<img src={`${process.env.PUBLIC_URL}/icons/arrange.svg`} alt="eye" />
							</td>
							<td>
								{item.isHidden && (
									<img
										className="cursor-pointer"
										src={`${process.env.PUBLIC_URL}/icons/eye-off.svg`}
										alt="eye"
										onClick={() => toggleHidden(item.uId)}
									/>
								)}
								{!item.isHidden && (
									<img
										className="cursor-pointer"
										src={`${process.env.PUBLIC_URL}/icons/eye.svg`}
										alt="eye"
										onClick={() => toggleHidden(item.uId)}
									/>
								)}
							</td>
							<td
								className={
									item.sizeError || duplicateLabels[item.uId] ? 'common-border-error' : ''
								}
								onClick={() => {
									setEditableUid(item.uId);
									setEditableFieldName('SIZE_LABEL');
								}}
								onBlur={() => {
									setEditableUid(null);
									setEditableFieldName('');
								}}
							>
								{editableUid === item.uId && editableFieldName === 'SIZE_LABEL' ? (
									<input
										autoFocus={true}
										type="text"
										autoComplete="off"
										name="sizeLabel"
										value={item.sizeLabel}
										onChange={(e) => handleChange(e, item.uId)}
									/>
								) : (
									item.sizeLabel
								)}
							</td>
							<td
								onClick={() => {
									setEditableUid(item.uId);
									setEditableFieldName('REMARKS');
								}}
								onBlur={() => {
									setEditableUid(null);
									setEditableFieldName('');
								}}
							>
								{editableUid === item.uId && editableFieldName === 'REMARKS' ? (
									<textarea
										autoFocus={true}
										value={item.remarks}
										name="remarks"
										onChange={(e) => handleChange(e, item.uId)}
									/>
								) : (
									item.remarks
								)}
								{tableData.length > 2 && !item?.id && (
									<button
										disabled={tableData.length < 3}
										className="button text size32 px-0 remove-row"
										onClick={() => removeRow(item)}
									>
										<img
											src={`${process.env.PUBLIC_URL}/icons/minus-round.svg`}
											alt="remove"
											className="row-hover-icon"
										/>
									</button>
								)}
							</td>
						</tr>
					)}
				</Draggable>
			);
		});
	};

	const onItemCLick = (obj) => {
		setInputData((prev) => ({ ...prev, baseSize: obj }));
		let baseSize = tableData?.find((item) => item.sizeLabel === obj.value);
		if (baseSize) {
			setBaseSizeUid(baseSize.uId);
		}
	};

	const getDropDownValues = () => {
		let options = [];
		tableData.forEach((item) => {
			if (item.sizeLabel) {
				options.push({
					id: item.sizeLabel,
					value: item.sizeLabel
				});
			}
		});

		return options;
	};

	const handleInputChange = (e) => {
		e.preventDefault();
		let { name, value } = e.target;
		setInputData((prev) => ({ ...prev, [name]: value }));
	};

	const isValid = () => {
		let errorObj = {};
		if (!inputData?.sizeName) {
			errorObj['sizeNameError'] = 'Size name is required!';
		}
		if (!inputData?.sizeRange) {
			errorObj['sizeRangeError'] = 'Size range is required!';
		}
		if (!inputData?.baseSize?.value) {
			errorObj['baseSizeError'] = 'Base size is required!';
		}
		setErrors(errorObj);
		return Object.keys(errorObj).length === 0;
	};

	const processDataForSubmit = () => {
		let postData = {};
		postData.name = inputData.sizeName;
		postData.range = inputData.sizeRange;
		postData.productId = id;
		let sizeList = [];
		let serial = 0;
		tableData.forEach((item) => {
			if (item.sizeLabel) {
				sizeList.push({
					serial: serial++,
					label: item.sizeLabel,
					isBaseSize: inputData?.baseSize?.value === item.sizeLabel,
					isDisabled: item.isHidden,
					remarks: item.remarks,
					id: item.id || null
				});
			}
		});
		postData.removeProductSizeIdList = removeProductSizeIdList;
		postData.productSizeList = sizeList;
		return postData;
	};

	const checkSizeLabelError = () => {
		let isSizeLabelMissing = false;
		let tmpTableData = tableData?.map((item) => {
			if (item.id && !item.sizeLabel) {
				item.sizeError = true;
				isSizeLabelMissing = true;
			} else {
				item.sizeError = false;
			}
			return item;
		});
		setTableData(tmpTableData);
		return isSizeLabelMissing === false;
	};

	const isSizeLabelAreUnique = () => {
		let uidList = {};
		for (let i = 0; i < tableData.length; i++) {
			for (let j = 1 + i; j < tableData.length; j++) {
				if (tableData[i].sizeLabel.toUpperCase() === tableData[j].sizeLabel.toUpperCase()) {
					uidList[tableData[i].uId] = 'Duplicate size label!';
					uidList[tableData[j].uId] = 'Duplicate size label!';
				}
			}
		}
		setDuplicateLabels(uidList);
		return Object.keys(uidList).length === 0;
	};

	const handleSubmit = async () => {
		if (isValid()) {
			try {
				dispatch(openLoader());
				if (!selectedId) {
					if (checkSizeLabelError() && isSizeLabelAreUnique()) {
						await addNewMeasurementSize(processDataForSubmit());
						await dispatch(fetchAllSizeCategories(`?productId=${id}`));
						if (onAddSuccess) await onAddSuccess();
						dispatch(closeLoader());
						onClose();
					} else {
						dispatch(closeLoader());
					}
				}
				if (selectedId) {
					if (checkSizeLabelError() && isSizeLabelAreUnique()) {
						await updateMeasurementSizeById(processDataForSubmit(), selectedId);
						await dispatch(fetchAllSizeCategories(`?productId=${id}`));
						if (onAddSuccess) await onAddSuccess();
						dispatch(closeLoader());
						onClose();
					} else {
						dispatch(closeLoader());
					}
				}
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const onDragEnd = (e) => {
		if (!e.destination) return;
		let cloneTableData = [...tableData];
		let sourceIndexData = cloneTableData[e.source.index];
		cloneTableData.splice(e.source.index, 1);
		insertAt(cloneTableData, e.destination.index, sourceIndexData);
		setTableData(cloneTableData);
	};

	return (
		<ModalContainer onCloseModal={onClose}>
			<div className="common-popup arrange-category-modal shadow-24dp">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte">
						<h3 className="semibold-16 mb-0">
							Add new size category for: {designToolStore?.brandName}
						</h3>
					</div>
					<div className="close-btn">
						<img
							src={`${process.env.PUBLIC_URL}/icons/close.svg`}
							alt="close"
							onClick={onClose}
						/>
					</div>
				</div>
				<div className="common-popup-body scollable-modal-body scroll-y-label">
					<div className="container px-0 pr-4">
						<div className="row">
							<div className="col-md-6 pr-6">
								<div className="form-group ">
									<label htmlFor="fitting-type">Size format name*</label>
									<input
										type="text"
										autoComplete="off"
										placeholder="Title"
										name="sizeName"
										value={inputData?.sizeName || ''}
										onChange={handleInputChange}
									/>
									{errors.sizeNameError && <p className="error">{errors.sizeNameError}</p>}
								</div>
							</div>

							<div className="col-md-3 pl-6 pr-6">
								<div className="form-group ">
									<label htmlFor="rise">Size range*</label>
									<input
										type="text"
										autoComplete="off"
										placeholder=""
										name="sizeRange"
										value={inputData?.sizeRange || ''}
										onChange={handleInputChange}
									/>
									{errors.sizeRangeError && (
										<p className="error">{errors.sizeRangeError}</p>
									)}
								</div>
							</div>

							<div className="col-md-3 pl-6">
								<div className="form-group ">
									<label htmlFor="rise">Base size*</label>

									<CustomDropdown
										type="addItem"
										title={'Select'}
										isShowLabel={false}
										onItemClick={(value) => onItemCLick(value)}
										items={getDropDownValues()}
										selectedItem={inputData?.baseSize?.value || ''}
									/>

									{errors.baseSizeError && <p className="error">{errors.baseSizeError}</p>}
								</div>
							</div>
						</div>
					</div>
					<div className="size-categorys-table pr-4">
						<table className="table with-border mb-1">
							<thead>
								<tr>
									<th>Arrange</th>
									<th>View</th>
									<th>Size labels</th>
									<th>Remarks</th>
								</tr>
							</thead>
							<DragDropContext onDragEnd={onDragEnd}>
								<Droppable droppableId="Tbody">
									{(provided) => (
										<tbody
											className="tbody-access"
											{...provided.droppableProps}
											ref={provided.innerRef}
										>
											{renderTableBody()}
										</tbody>
									)}
								</Droppable>
							</DragDropContext>
						</table>
						<button className="button text size32 px-0" onClick={addNewRow}>
							<span className="mr-1">Add</span>
							<img src={`${process.env.PUBLIC_URL}/icons/add-round.svg`} alt="add" />
						</button>
					</div>
				</div>

				<div className="modal-footer">
					<div className="footer-buttons text-right">
						<button className="button gray-outline size32 ml-2" onClick={onClose}>
							Cancel
						</button>
						<button className="button size32 ml-2" onClick={handleSubmit}>
							Submit
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default AddSizeCategory;
